import { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { AlertBadge, GoBack, LayoutContext, Tabs, TutorialDialog } from '@/_shared/components';
import { TutorialKeys } from '@/_shared/constants';
import { useTutorialCheck } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useTabs } from '@/_shared/hooks/useTabs';
import {
  getDepartmentTeamDetailsCall,
  getDepartmentTeamMembersCall,
} from '@/departments/state/api';
import { getEmployeesMeCall } from '@/profile/state/api';
import { TabDepartmentTeam, TeamAssessment } from '../components';

export const TeamScreen = () => {
  const t = useTranslations('department');
  const tShared = useTranslations();
  const { openDialog: openDialogManagerTutorial } = useDialog('managerTeamTutorial');
  const { setLayout } = useContext(LayoutContext);
  const { teamId: teamIdParams } = useParams();
  const { hasAlreadySeenTutorial } = useTutorialCheck();

  const {
    success: successGetEmployeesMe,
    error: errorGetEmployeesMe,
    leadership,
    hasManagerLeader,
    hasTeamLeader,
    response: responseGetEmployeesMe,
  } = getEmployeesMeCall.hook();

  const { execute: executeGetDepartmentTeamMembers, reset: resetGetDepartmentTeamMembers } =
    getDepartmentTeamMembersCall.hook();

  const { execute: executeGetDepartmentTeamDetails, reset: resetGetDepartmentTeamDetails } =
    getDepartmentTeamDetailsCall.hook();

  const departmentId = useMemo(() => {
    return hasManagerLeader ? leadership?.department?.id : responseGetEmployeesMe?.department?.id;
  }, [hasManagerLeader, leadership, responseGetEmployeesMe]);

  const teamId = useMemo(() => {
    return hasManagerLeader ? teamIdParams : leadership?.team?.id;
  }, [hasManagerLeader, teamIdParams, leadership]);

  const tabsInitialState = [
    {
      title: t('tabs.overviewTeam'),
      index: 0,
      active: true,
      dataTestId: 'team',
    },
    {
      title: t('tabs.assessment'),
      index: 1,
      active: false,
      dataTestId: 'assessment',
    },
  ];
  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  const slides = [
    {
      title: t('tutorial.team.slides.slide1.title'),
      description: t('tutorial.team.slides.slide1.description'),
      assetName: 'tutorialLearningTeamSlide1',
    },
    {
      title: t('tutorial.team.slides.slide2.title'),
      description: t('tutorial.team.slides.slide2.description'),
      assetName: 'tutorialLearningTeamSlide2',
    },
    {
      title: t('tutorial.team.slides.slide3.title'),
      description: t('tutorial.team.slides.slide3.description'),
      assetName: 'tutorialLearningTeamSlide3',
    },
  ];

  useEffect(() => {
    setLayout({ title: hasManagerLeader ? 'department' : 'department.team' });
  }, [hasManagerLeader, setLayout, t]);

  useEffect(() => {
    if (teamId && departmentId) {
      executeGetDepartmentTeamMembers({ teamId, departmentId, excludeMemberType: ['teamManager'] });
      executeGetDepartmentTeamDetails({ teamId, departmentId });
    }
    return () => {
      resetGetDepartmentTeamMembers();
      resetGetDepartmentTeamDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, teamId]);

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.ManagerTeam)) {
      openDialogManagerTutorial();
    }
  }, [hasAlreadySeenTutorial, openDialogManagerTutorial]);

  if ((!departmentId && successGetEmployeesMe) || errorGetEmployeesMe) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  return (
    <Box>
      <Box>
        {hasManagerLeader ? <GoBack /> : null}
        <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
        {tabs.map(tab => {
          return (
            <Box key={tab.title} display='flex' flexDirection='column'>
              {tab.index === 0 && tab.active && (
                <>
                  {hasTeamLeader && (
                    <Box mt='s'>
                      <AlertBadge iconName='info' type='primary' label={t('dataMismatchWarning')} />
                    </Box>
                  )}
                  <TabDepartmentTeam />
                </>
              )}
              {tab.index === 1 && tab.active && (
                <TeamAssessment departmentId={departmentId} teamId={teamId} />
              )}
            </Box>
          );
        })}
      </Box>
      <TutorialDialog
        translationsKey='department.tutorial.team'
        dialogKey='managerTeamTutorial'
        slides={slides}
      />
    </Box>
  );
};
